import { useState } from "react";
import { useParams } from "react-router-dom";
import "./EmailForm.scss";
import { appURL, datenSchutzBlobURL, sendConfirmationEmailFunctionURL } from "../../Constants";
import { encryptQueryParam } from "../../services/FormService";
import { deleteToken } from "../../services/ApiService";

interface FormData {
    email1: string;
    email2: string;
}

const EmailForm: React.FC = () => {
    const { tokenID } = useParams();
    const [formData, setFormData] = useState<FormData>({ email1: "", email2: "" });
    const [errors, setErrors] = useState<{ email1?: string; email2?: string }>({});
    const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

    const handleCancel = async () => {
        try {
            await deleteToken(tokenID!);
            // After deletion, reload to trigger token validation and show error message
            window.location.reload();
        } catch (error) {
            // Optionally, show an error if deletion failed
            alert("Fehler beim Abbrechen des Vorgangs. Bitte versuchen Sie es erneut.");
        }
    };

    const validateEmail = (email: string): boolean => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const validateForm = (): boolean => {
        const newErrors: { email1?: string; email2?: string } = {};

        if (!formData.email1) {
            newErrors.email1 = "E-Mail ist erforderlich";
        } else if (!validateEmail(formData.email1)) {
            newErrors.email1 = "Ungültiges E-Mail-Format";
        }

        if (!formData.email2) {
            newErrors.email2 = "E-Mail-Bestätigung ist erforderlich";
        } else if (formData.email1 !== formData.email2) {
            newErrors.email2 = "Emails stimmen nicht überein";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setSubmitStatus("loading");

        try {
            const response = await fetch(sendConfirmationEmailFunctionURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    appURL: `${appURL}/${tokenID}/confirm/${encodeURIComponent(encryptQueryParam(formData.email1))}`,
                    email: formData.email1,
                    tokenID: tokenID
                }),
            });

            if (!response.ok) throw new Error("Submission failed");

            setSubmitStatus("success");
        } catch (error) {
            console.error("Error submitting form:", error);
            setSubmitStatus("error");
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    return (
        <div className="email-form">
            <p>
                Lieber MediaMarktSaturn-Kunde,
            </p>
            <br />
            <p>
                wir freuen uns, dass Sie sich für einen Service an unserer Smartbar entschieden haben. Damit wir den von Ihnen gewählten Service durchführen können, benötigen wir personenbezogene Daten von Ihnen, die Sie unseren
                &nbsp;
                <a
                    className="privacy-link"
                    href={datenSchutzBlobURL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Datenschutzinformation
                </a>
                &nbsp;
                entnehmen können.
            </p>
            <p>
                Ihre Daten werden nur für die Dauer der Serviceerbringung gespeichert und danach innerhalb von 24 Stunden gelöscht.
            </p>
            <p>
                Bitte geben Sie unten Ihre Mail-Adresse ein und klicken Sie auf „Bestätigen“.            </p>
            <p>
                Sie erhalten nun eine Mail, über die Sie bestätigen können, dass wir Ihre personenbezogenen Daten für die Durchführung des gewählten Service nutzen dürfen.
            </p>

            <form style={{ width: "90%", margin: "20px" }} onSubmit={handleSubmit}>
                {submitStatus === "success" ? (
                    <div className="success-message">
                        Gesendet! Bitte überprüfen Sie Ihre Mail- und Spam-Ordner                    </div>
                ) : submitStatus === "error" ? (
                    <div className="error-message">
                        Fehler beim Senden der Bestätigungs-E-Mail
                    </div>
                ) : (
                    <>
                        <div className="form-group">
                            <label>E-Mail-Adresse</label>
                            <input
                                type="email"
                                name="email1"
                                value={formData.email1}
                                onChange={handleChange}
                                required
                            />
                            {errors.email1 && (
                                <small className="error-message">{errors.email1}</small>
                            )}
                        </div>

                        <div className="form-group">
                            <label>E-Mail-Adresse bestätigen</label>
                            <input
                                type="email"
                                name="email2"
                                value={formData.email2}
                                onChange={handleChange}
                                required
                            />
                            {errors.email2 && (
                                <small className="error-message">{errors.email2}</small>
                            )}
                        </div>
                        <button type="submit" disabled={submitStatus === "loading"}>
                            {submitStatus === "loading" ? "Laden..." : "Bestätigen"}
                        </button>
                        <button type="button" onClick={handleCancel} style={{ backgroundColor: "red", marginBottom: 0 }}>
                            Abbrechen
                        </button>
                        <small style={{ color: "#777" }}>Durch das Abbrechen wird der Vorgang gestoppt und Ihre Daten werden nicht weiter verwendet</small>
                    </>
                )}
            </form>
        </div>
    );
};

export default EmailForm;